import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/joy';
import { IconWrapper } from '../../../components/IconWrapper';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { pdfjsWorkerSrc } from '../../../utils/pdfjsWorkerSrc';
import { shouldUseScrollablePdfPreview } from '../utils/mobileWebView';

type PreviewState = 'idle' | 'rendering' | 'ready' | 'failed';

type PublicDocPdfPreviewProps = {
  previewPdfUrl: string | null;
  pdfUrl: string | null;
  pdfHtml: string | null;
  previewPdfState: PreviewState;
};

export default function PublicDocPdfPreview({
  previewPdfUrl,
  pdfUrl,
  pdfHtml,
  previewPdfState,
}: PublicDocPdfPreviewProps) {
  const src = previewPdfUrl || pdfUrl || null;
  const useScrollablePreview = shouldUseScrollablePdfPreview();
  const [pageImages, setPageImages] = useState<string[]>([]);
  const [canvasState, setCanvasState] = useState<'idle' | 'loading' | 'ready' | 'failed'>('idle');

  useEffect(() => {
    setPageImages([]);
    setCanvasState('idle');
    if (!useScrollablePreview || !src) return;

    let cancelled = false;
    setCanvasState('loading');

    (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorkerSrc;
        const pdf = await pdfjs.getDocument(src).promise;
        const images: string[] = [];
        const scale = Math.min(2, Math.max(1.1, window.devicePixelRatio || 1));

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
          if (cancelled) return;
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          if (!context) throw new Error('Canvas unavailable');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({ canvas, canvasContext: context, viewport }).promise;
          images.push(canvas.toDataURL('image/jpeg', 0.9));
        }

        if (!cancelled) {
          setPageImages(images);
          setCanvasState('ready');
        }
      } catch {
        if (!cancelled) setCanvasState('failed');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [src, useScrollablePreview]);

  const openFullPdf = () => {
    if (!src) return;
    window.open(src, '_blank', 'noopener,noreferrer');
  };

  const frameShellSx = {
    border: '1px solid rgba(0,0,0,0.10)',
    borderRadius: 'md',
    bgcolor: '#fff',
    boxShadow: 'sm',
  } as const;

  if (!src && !pdfHtml) return null;

  if (previewPdfState === 'rendering' && !src) {
    return (
      <Box sx={{ ...frameShellSx, minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography level="body-sm" sx={{ color: 'rgba(0,0,0,0.55)' }}>
          Preparing PDF preview…
        </Typography>
      </Box>
    );
  }

  if (useScrollablePreview && src) {
    return (
      <Stack spacing={1}>
        <Box
          sx={{
            ...frameShellSx,
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            maxHeight: { xs: 'none', md: '80vh' },
            p: { xs: 0.75, sm: 1.25 },
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
          }}
        >
          {canvasState === 'loading' || (canvasState === 'idle' && pageImages.length === 0) ? (
            <Stack sx={{ minHeight: 240 }} alignItems="center" justifyContent="center" spacing={0.75}>
              <Typography level="body-sm" sx={{ color: 'rgba(0,0,0,0.55)' }}>
                Loading document pages…
              </Typography>
            </Stack>
          ) : canvasState === 'failed' ? (
            <Stack spacing={1.25} sx={{ p: 1 }}>
              <Typography level="body-sm" sx={{ color: 'rgba(0,0,0,0.65)' }}>
                Inline preview is unavailable in this browser. Open the full PDF to view every page.
              </Typography>
              <Button
                size="sm"
                variant="solid"
                startDecorator={<IconWrapper icon={FaExternalLinkAlt} />}
                onClick={openFullPdf}
              >
                Open full PDF
              </Button>
            </Stack>
          ) : (
            <Stack spacing={1.25}>
              {pageImages.map((imageSrc, index) => (
                <Box
                  key={`pdf-page-${index + 1}`}
                  component="img"
                  src={imageSrc}
                  alt={`Document page ${index + 1}`}
                  sx={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    borderRadius: 'sm',
                    border: '1px solid rgba(0,0,0,0.06)',
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>
        <Button
          size="sm"
          variant="outlined"
          startDecorator={<IconWrapper icon={FaExternalLinkAlt} />}
          onClick={openFullPdf}
          sx={{ alignSelf: 'flex-start' }}
        >
          Open full PDF
        </Button>
      </Stack>
    );
  }

  if (previewPdfState === 'failed' && pdfHtml) {
    return (
      <Box
        sx={{
          ...frameShellSx,
          height: { xs: 'auto', md: 720 },
          maxHeight: { xs: 'none', md: 720 },
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Box
          component="iframe"
          title="Document Preview (HTML fallback)"
          srcDoc={pdfHtml}
          sx={{
            display: 'block',
            width: '100%',
            minHeight: { xs: 480, md: 720 },
            height: { xs: 'auto', md: '100%' },
            border: 0,
          }}
        />
      </Box>
    );
  }

  if (src) {
    return (
      <Box
        sx={{
          ...frameShellSx,
          height: { xs: 560, md: 720 },
          overflow: 'hidden',
        }}
      >
        <Box
          component="iframe"
          title="Document Preview"
          src={src}
          sx={{ width: '100%', height: '100%', border: 0 }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ ...frameShellSx, minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography level="body-sm" sx={{ color: 'rgba(0,0,0,0.55)' }}>
        Preparing PDF preview…
      </Typography>
    </Box>
  );
}
